import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { logError } from "@/lib/log";
import { Badge } from "@/components/ui/badge";
import { Crown, Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";

const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" }) : "");

export default function AdminFounders() {
  const [data, setData] = useState(null);
  const [held, setHeld] = useState({});
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/admin/founders").then((r) => { setData(r.data); setHeld(Object.fromEntries(r.data.places.map((p) => [p.speciality, p.held]))); }).catch((e) => logError("admin-founders", e)); }, []);
  const saveHeld = async () => {
    setSaving(true);
    try { const { data: d } = await api.put("/admin/founders/held", { held }); setData((x) => ({ ...x, places: d.places })); toast.success("Held places updated"); }
    catch { toast.error("Could not save"); } finally { setSaving(false); }
  };
  if (!data) return <Loader2 className="w-5 h-5 animate-spin text-primary" />;
  return (
    <div className="bg-card border border-border rounded-2xl p-5" data-testid="admin-founders">
      <p className="font-display font-semibold mb-1 flex items-center gap-2"><Crown className="w-4 h-4 text-amber-500" /> Founders Club</p>
      <p className="text-xs text-muted-foreground mb-4">100 places — 20 per speciality. Fully subscribed members get 50% off for as long as they stay fully subscribed. A place is reserved at sign-up and numbered/counted from the first payment; cancelling in the 12-month term forfeits it.</p>
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-2 mb-5">
        {data.places.map((p) => (
          <div key={p.speciality} className="rounded-xl border border-border p-3" data-testid={`admin-founders-place-${p.speciality.toLowerCase()}`}>
            <p className="text-[11px] text-muted-foreground truncate">{p.label}</p>
            <p className="font-display text-xl font-extrabold">{p.taken}<span className="text-sm text-muted-foreground font-normal">/{p.total}</span> {p.reserved > 0 && <span className="text-xs text-amber-700 font-semibold">+{p.reserved} reserved</span>}</p>
            <div className="h-1.5 rounded-full bg-secondary mt-1.5 overflow-hidden"><div className="h-full bg-amber-500" style={{ width: `${(p.taken / p.total) * 100}%` }} /></div>
            <p className="text-[10px] text-muted-foreground mt-1">{p.paid} paid · {p.held} held</p>
          </div>
        ))}
      </div>
      <div className="rounded-xl border border-amber-200 bg-amber-50/50 p-3 mb-5" data-testid="admin-founders-held">
        <p className="text-xs font-semibold mb-1">Held places (allocated outside the app)</p>
        <p className="text-[11px] text-muted-foreground mb-2">Places you've promised to pilot partners or agreed offline. They show as taken on the public counters and reduce what's available in the app.</p>
        <div className="flex flex-wrap items-end gap-2">
          {data.places.map((p) => (
            <label key={p.speciality} className="text-[11px] text-muted-foreground">{p.speciality}
              <Input type="number" min={0} max={p.total} value={held[p.speciality] ?? 0} onChange={(e) => setHeld({ ...held, [p.speciality]: Number(e.target.value) })} className="w-20 h-8 mt-0.5" data-testid={`admin-founders-held-${p.speciality.toLowerCase()}`} />
            </label>
          ))}
          <Button size="sm" onClick={saveHeld} disabled={saving} data-testid="admin-founders-held-save">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Save className="w-3.5 h-3.5 mr-1.5" /> Save</>}</Button>
        </div>
      </div>
      {data.members.length === 0 ? <p className="text-sm text-muted-foreground" data-testid="admin-founders-empty">No founders yet.</p> : (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-xs text-muted-foreground"><th className="pb-2">#</th><th className="pb-2">Member</th><th className="pb-2">Speciality</th><th className="pb-2">Joined</th><th className="pb-2">Status</th></tr></thead>
            <tbody>
              {data.members.map((m) => (
                <tr key={m.id} className="border-t border-border" data-testid={`admin-founder-row-${m.id}`}>
                  <td className="py-2 font-bold text-amber-700">{m.number ? `#${m.number}` : "—"}</td>
                  <td className="py-2"><p className="font-medium">{m.name}</p><p className="text-xs text-muted-foreground">{m.email}{m.org_name ? ` · ${m.org_name}` : ""}</p></td>
                  <td className="py-2 text-xs">{m.speciality_label}</td>
                  <td className="py-2 text-xs">{fmt(m.joined_at)}</td>
                  <td className="py-2">
                    <div className="flex gap-1 flex-wrap">
                      <Badge className={m.status === "confirmed" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : m.status === "forfeited" ? "bg-rose-100 text-rose-800 hover:bg-rose-100" : "bg-amber-100 text-amber-900 hover:bg-amber-100"}>{m.status === "confirmed" ? "Confirmed · paying" : m.status === "forfeited" ? `Forfeited (${m.forfeit_reason})` : "Reserved · awaiting first payment"}</Badge>
                      {m.pack_discount_used && <Badge variant="outline">Pack discount used</Badge>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
